/* eslint-disable @typescript-eslint/no-unsafe-assignment -- Any types will be cast properly to its respective type */

import type { IMsalContext } from '@azure/msal-react';
import { flow, types } from 'mobx-state-tree';

import type { Response } from 'watchtower-ui/api/api';
import { httpCall } from 'watchtower-ui/api/api';
import type { LayoutType } from 'watchtower-ui/components/Layout/Layout';
import { getRootStore } from 'watchtower-ui/models/store/utils';

const SCENARIO_API_URL = `/v1/user-scenarios/scenarios`;

type TScenarioTab = 'WATERFALL' | 'SPEND_EFFICIENCY' | 'INPUT_IMPACT_INSIGHTS' | 'CONTROL_PANEL' | 'SATURATION_CURVES';

export interface IScenario {
  scenario_id: number;
  scenario_name: string;
  scenario_tab: TScenarioTab;
  filters: string;
  user_id: string;
  is_default: boolean;
}

export const ScenarioTabMappings: Record<LayoutType, TScenarioTab> = {
  waterfall: 'WATERFALL',
  kpi: 'SPEND_EFFICIENCY',
  saturationcurve: 'SATURATION_CURVES',
};

type TScenarioFull = Partial<Record<TScenarioTab, IScenario[]>>;

export const ScenariosModel = types
  .model('Scenarios')
  .props({
    list: types.optional(types.frozen<TScenarioFull>(), {}),
  })
  .views((self) => ({
    favorite: (type: TScenarioTab): IScenario | undefined => self.list[type]?.find((item) => item.is_default),
  }))
  .volatile<{
    loading: boolean;
    modal: boolean;
    active: string | undefined;
  }>(() => ({
    loading: false,
    modal: false,
    active: undefined,
  }))
  .views((self) => ({
    get activeScenario(): IScenario | undefined {
      if (!self.active) {
        return undefined;
      }

      const scenario = self.list[ScenarioTabMappings.waterfall]?.find((item) => item.scenario_name === self.active);

      return scenario;
    },
  }))
  .actions((self) => ({
    setLoading: (value: boolean) => {
      self.loading = value;
    },
    setModal: (value: boolean) => {
      self.modal = value;
    },
    setActive: (scenarioName: string | undefined) => {
      self.active = scenarioName;
    },
  }))
  .actions((self) => ({
    load: flow(function* load() {
      const rootStore = getRootStore(self);
      const msal = rootStore.core.msalDetails as IMsalContext;

      self.loading = true;

      try {
        const response: Response<string> = yield httpCall<string>(
          {
            url: SCENARIO_API_URL,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          },
          msal,
        );

        if (!response.success) {
          throw new Error(response.message);
        }

        const list = response.data as TScenarioFull | undefined;

        if (list) {
          self.list = list;
        }
      } catch (error) {
        console.error('Error loading scenarios', error);
      } finally {
        self.loading = false;
      }
    }),
  }))
  .actions((self) => ({
    add: flow(function* add(value: Partial<IScenario>) {
      const rootStore = getRootStore(self);
      const msal = rootStore.core.msalDetails as IMsalContext;

      const data: Partial<IScenario> = {
        is_default: false,
        ...value,
      };

      self.loading = true;

      try {
        const response: Response<string> = yield httpCall<string>(
          {
            url: SCENARIO_API_URL,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data,
          },
          msal,
        );

        if (!response.success) {
          throw new Error(response.message);
        }

        yield self.load();

        self.loading = false;

        return {
          status: true,
          message: 'Scenario added successfully',
        };
      } catch (error) {
        console.error('Error adding scenarios', error);
        self.loading = false;
        return {
          status: false,
          message: (error as Error).message,
        };
      }
    }),
    remove: flow(function* remove(scenarioId: number) {
      const rootStore = getRootStore(self);
      const msal = rootStore.core.msalDetails as IMsalContext;

      self.loading = true;

      try {
        const response: Response<string> = yield httpCall<string>(
          {
            url: `${SCENARIO_API_URL}/${scenarioId}`,
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          },
          msal,
        );

        if (!response.success) {
          throw new Error(response.message);
        }

        yield self.load();

        self.loading = false;

        return {
          status: true,
          message: 'Scenario removed successfully',
        };
      } catch (error) {
        console.error('Error removing scenarios', error);
        self.loading = false;
        return {
          status: false,
          message: (error as Error).message,
        };
      }
    }),
    setFavorite: flow(function* setFavorite(scenarioId: number, scenarioName: string, set: boolean = true) {
      const rootStore = getRootStore(self);
      const msal = rootStore.core.msalDetails as IMsalContext;

      self.loading = true;

      try {
        const response: Response<string> = yield httpCall<string>(
          {
            url: `${SCENARIO_API_URL}/${scenarioId}`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: {
              scenario_name: scenarioName,
              is_default: set,
            },
          },
          msal,
        );

        if (!response.success) {
          throw new Error(response.message);
        }

        yield self.load();

        self.loading = false;

        return {
          status: true,
          message: 'Scenario marked as default',
        };
      } catch (error) {
        console.error('Error marking scenarios as default', error);
        self.loading = false;
        return {
          status: false,
          message: (error as Error).message,
        };
      }
    }),
  }));
